import { defineStore } from 'pinia'

import { useAsyncRequest } from '@/composables/useAsyncRequest'
import { setOgTags } from '@/utils/metaTags'

export const useProductStore = defineStore('Product', () => {
    const router = useRouter()
    const route = useRoute()

    // -----------------------------------------------------
    // State
    // -----------------------------------------------------
    const item = ref(null)
    const relatedProducts = ref([])

    const params = reactive({})

    // Async request handler
    const { loading, error, request } = useAsyncRequest()

    // -----------------------------------------------------
    // Load API
    // -----------------------------------------------------

    // The server fetches this same product — with the same query params —
    // for the OG tags, and embeds it in the page; reuse it instead of
    // fetching it again. Only valid for the slug it was embedded for, and
    // only once: consumed here so a later re-fetch (e.g. after changing
    // dates) always hits the API.
    const readEmbeddedProduct = (slug) => {
        const el = document.getElementById('hub-product-data')
        if (!el || el.dataset.slug !== slug) return null

        el.remove()

        try {
            return JSON.parse(el.textContent)
        } catch {
            return null
        }
    }

    const loadData = async () => {
        const embedded = readEmbeddedProduct(route.params.slug)
        const response = embedded ?? (await request('get', '/api/products/' + encodeURIComponent(route.params.slug), { params }))

        if (response) {
            item.value = response.data
            relatedProducts.value = response.relatedProducts

            setOgTags({
                title: item.value.name,
                description: item.value.property?.description || item.value.description,
                image: item.value.cover?.url || item.value.gallery?.[0]?.url || item.value.property?.gallery?.[0]?.url
            })
        }
    }

    // -----------------------------------------------------
    // Initialize params from route
    // -----------------------------------------------------
    const initialize = async () => {
        await router.isReady()

        Object.assign(params, route.query)

        await loadData()
    }

    // -----------------------------------------------------
    // Expose store
    // -----------------------------------------------------
    return {
        item,
        relatedProducts,
        related_products: relatedProducts,
        loading,
        error,
        params,
        initialize
    }
})

export const useProductstore = useProductStore
